import { ErrorMessage, Field, FieldArray, Formik, Form as FormikForm } from 'formik';
import { Spinner } from 'react-bootstrap';
import { IoIosArrowRoundBack, IoIosCloseCircleOutline } from 'react-icons/io';
import { NumericFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import * as Yup from 'yup';
import './GroupRatesForm.scss';
import plusIcon from '@icons/PlusWhite.svg';
import FormikNumberFormat from '../../../../FormikNumberFormat/FormikNumberFormat';

const GroupRatesForm = ({ changeCurrentStep, step1Values, setStep1Values, zoneOptions }) => {
    const reactSelectStyle = {
        control: (base, state) => ({
            ...base,
            boxShadow: 0,
            border: '1px solid #aeadad'
        })
    };

    const initialValues = {
        subscriptions: step1Values.subscriptions,
        validations: step1Values.validations
    };

    const validationSchema = Yup.object().shape({
        subscriptions: Yup.array().of(
            Yup.object().shape({
                zone: Yup.string().required('Zone is required'),
                subscription: Yup.string().required('Subscription is required'),
                specialRate: Yup.number()
                    .min(0, 'Special rate cannot be negative')
                    .required('Special rate is required'),
                parkerMinimum: Yup.number()
                    // .min(1, 'Minimum must be at least 1')
                    .max(Yup.ref('parkerMaximum'), 'Parker minimum cannot be greater than maximum'),
                // .required('Minimum is required'),
                parkerMaximum: Yup.number().required('Maximum is required')
            })
        ),
        validations: Yup.array().of(
            Yup.object().shape({
                zoneValidation: Yup.string().required('Zone validation is required'),
                discount: Yup.number().min(0, 'Discount cannot be negative').required('Discount is required'),
                isFixedPrice: Yup.boolean(),
                maximumDuration: Yup.number()
                    .min(1, 'Duration must be at least 1 hour')
                    .required('Maximum duration is required'),
                monthlyMaximum: Yup.number(),
                isHourlyMaximumDuration: Yup.boolean(),
                _id: Yup.string()
            })
        )
    });

    const goBack = () => {
        changeCurrentStep(0);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const newValues = { ...values };
            if (newValues?.validations?.length > 0) {
                newValues.validations = newValues.validations?.map((validation) => {
                    if (validation.isHourlyMaximumDuration) {
                        validation.maximumDuration = validation.maximumDuration * 60;
                    }
                    return validation;
                });
            }
            setStep1Values({ ...newValues, isCompleted: true });
            changeCurrentStep(2, false);
            setSubmitting(false);
        } catch (error) {
            setSubmitting(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={true}
            validateOnBlur={true}
        >
            {({ isSubmitting, setFieldValue, values }) => (
                <FormikForm className="group-rates-form">
                    <div className="details-heading">Subscriptions</div>
                    <FieldArray name="subscriptions">
                        {({ push, remove }) => (
                            <>
                                {values.subscriptions.map((subscription, index) => {
                                    // Get the selected location from zoneOptions using the selected zone value
                                    const selectedLocation = zoneOptions.find(
                                        (option) => option.value === subscription.zone
                                    );
                                    // Map the recurringRates into select options if available
                                    const recurringRateOptions =
                                        selectedLocation && selectedLocation.recurringRates
                                            ? selectedLocation?.recurringRates?.map((rr) => ({
                                                  value: rr?._id,
                                                  label: `${rr?.rateName} - ${rr?.frequency}`
                                              }))
                                            : [];

                                    return (
                                        <div key={index} className="rate-section w-100">
                                            <div className="remove-btn mb-3">
                                                <IoIosCloseCircleOutline
                                                    onClick={() => {
                                                        if (values.subscriptions.length > 1) {
                                                            remove(index);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="fields-row">
                                                <div className="field-container">
                                                    <label>Zone*</label>
                                                    <Select
                                                        name={`subscriptions[${index}].zone`}
                                                        options={zoneOptions}
                                                        styles={reactSelectStyle}
                                                        value={zoneOptions.find((z) => z.value === subscription.zone)}
                                                        onChange={(selected) => {
                                                            setFieldValue(
                                                                `subscriptions[${index}].zone`,
                                                                selected.value
                                                            );
                                                            setFieldValue(`subscriptions[${index}].subscription`, null);
                                                        }}
                                                        className="multi-select-field"
                                                        classNamePrefix="__select-days"
                                                    />
                                                    <ErrorMessage
                                                        name={`subscriptions[${index}].zone`}
                                                        component="span"
                                                        className="validation-error"
                                                    />
                                                </div>
                                                <div className="field-container">
                                                    <label>Subscription*</label>
                                                    <Select
                                                        name={`subscriptions[${index}].subscription`}
                                                        options={recurringRateOptions}
                                                        styles={reactSelectStyle}
                                                        value={
                                                            recurringRateOptions.find(
                                                                (s) =>
                                                                    s.value ===
                                                                    (subscription.originalRate
                                                                        ? String(subscription.originalRate)
                                                                        : String(subscription.subscription))
                                                            ) || null
                                                        }
                                                        onChange={(selected) =>
                                                            setFieldValue(
                                                                `subscriptions[${index}].subscription`,
                                                                selected.value
                                                            )
                                                        }
                                                        className="multi-select-field"
                                                        classNamePrefix="__select-days"
                                                        placeholder={
                                                            recurringRateOptions.length === 0
                                                                ? 'Select zone first'
                                                                : 'Select Subscription'
                                                        }
                                                    />
                                                    <ErrorMessage
                                                        name={`subscriptions[${index}].subscription`}
                                                        component="span"
                                                        className="validation-error"
                                                    />
                                                </div>
                                                <div className="field-container">
                                                    <label htmlFor={`subscriptions[${index}].specialRate`}>
                                                        Special Rate*
                                                    </label>
                                                    <FormikNumberFormat
                                                        name={`subscriptions[${index}].specialRate`}
                                                        setFieldValue={setFieldValue}
                                                        value={subscription.specialRate}
                                                        thousandSeparator={true}
                                                        decimalScale={2}
                                                        fixedDecimalScale={true}
                                                        prefix="$"
                                                        placeholder="$0.00"
                                                    />
                                                    <ErrorMessage
                                                        name={`subscriptions[${index}].specialRate`}
                                                        component="span"
                                                        className="validation-error"
                                                    />
                                                </div>
                                                <div className="field-container">
                                                    <label>Parker Minimum</label>
                                                    <Field
                                                        name={`subscriptions[${index}].parkerMinimum`}
                                                        type="number"
                                                        min={0}
                                                    />
                                                    <ErrorMessage
                                                        name={`subscriptions[${index}].parkerMinimum`}
                                                        component="span"
                                                        className="validation-error"
                                                    />
                                                </div>
                                                <div className="field-container">
                                                    <label>Parker Maximum*</label>
                                                    <Field
                                                        name={`subscriptions[${index}].parkerMaximum`}
                                                        type="number"
                                                        min={0}
                                                    />
                                                    <ErrorMessage
                                                        name={`subscriptions[${index}].parkerMaximum`}
                                                        component="span"
                                                        className="validation-error"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="button-container">
                                    <button
                                        type="button"
                                        className="add-section-btn"
                                        onClick={() =>
                                            push({
                                                zone: '',
                                                subscription: '',
                                                specialRate: '',
                                                parkerMinimum: 0,
                                                parkerMaximum: ''
                                            })
                                        }
                                    >
                                        <img className="me-2 mb-1" src={plusIcon} alt="plus icon" /> Add Another
                                    </button>
                                </div>
                            </>
                        )}
                    </FieldArray>

                    <div className="details-heading">Validations</div>
                    <FieldArray name="validations">
                        {({ push, remove }) => (
                            <>
                                {values.validations.map((validation, index) => (
                                    <div key={index} className="rate-section w-100">
                                        <div className="remove-btn mb-3">
                                            <IoIosCloseCircleOutline
                                                onClick={() => {
                                                    if (values.validations.length > 1) {
                                                        remove(index);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="fields-row">
                                            <div className="field-container">
                                                <label>Zone Validation*</label>
                                                <Select
                                                    name={`validations[${index}].zoneValidation`}
                                                    options={zoneOptions}
                                                    styles={reactSelectStyle}
                                                    value={zoneOptions.find(
                                                        (z) => z.value === validation.zoneValidation
                                                    )}
                                                    onChange={(selected) =>
                                                        setFieldValue(
                                                            `validations[${index}].zoneValidation`,
                                                            selected.value
                                                        )
                                                    }
                                                    className="multi-select-field"
                                                    classNamePrefix="__select-days"
                                                />
                                                <ErrorMessage
                                                    name={`validations[${index}].zoneValidation`}
                                                    component="span"
                                                    className="validation-error"
                                                />
                                            </div>
                                            <div className="field-container">
                                                <label>Discount*</label>
                                                <div className="discount-input-group">
                                                    <NumericFormat
                                                        name={`validations[${index}].discount`}
                                                        placeholder="Enter discount"
                                                        decimalScale={2}
                                                        fixedDecimalScale
                                                        value={validation.discount}
                                                        prefix={validation.isFixedPrice ? '$' : ''}
                                                        suffix={validation.isFixedPrice ? '' : '%'}
                                                        onValueChange={(values) =>
                                                            setFieldValue(
                                                                `validations[${index}].discount`,
                                                                values.value
                                                            )
                                                        }
                                                    />
                                                    <div className="discount-toggle">
                                                        <label className="switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={validation?.isFixedPrice}
                                                                onChange={(e) =>
                                                                    setFieldValue(
                                                                        `validations[${index}].isFixedPrice`,
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                            <span className="slider">
                                                                <p
                                                                    className={` discount-value ${validation.isFixedPrice ? '' : 'justify-content-end'}`}
                                                                >
                                                                    {validation.isFixedPrice ? '$' : '%'}
                                                                </p>
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>
                                                <ErrorMessage
                                                    name={`validations[${index}].discount`}
                                                    component="span"
                                                    className="validation-error"
                                                />
                                            </div>
                                            <div className="field-container">
                                                <label>Maximum Duration*</label>
                                                <div className="discount-input-group">
                                                    <Field
                                                        name={`validations[${index}].maximumDuration`}
                                                        type="number"
                                                        min={0}
                                                    />

                                                    <div className="discount-toggle">
                                                        <div
                                                            className={`toggle-switch ${validation?.isHourlyMaximumDuration ? 'toggle-inactive' : 'toggle-active'}`}
                                                        >
                                                            <div
                                                                className="toggle-option hours"
                                                                onClick={() => {
                                                                    if (!validation?.isHourlyMaximumDuration) {
                                                                        setFieldValue(
                                                                            `validations[${index}].isHourlyMaximumDuration`,
                                                                            true
                                                                        );
                                                                        setFieldValue(
                                                                            `validations[${index}].maximumDuration`,
                                                                            (validation?.maximumDuration || 0) / 60
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                Hours
                                                            </div>
                                                            <div
                                                                className="toggle-option minutes"
                                                                onClick={() => {
                                                                    if (validation?.isHourlyMaximumDuration) {
                                                                        setFieldValue(
                                                                            `validations[${index}].isHourlyMaximumDuration`,
                                                                            false
                                                                        );
                                                                        setFieldValue(
                                                                            `validations[${index}].maximumDuration`,
                                                                            (validation?.maximumDuration || 0) * 60
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                Minutes
                                                            </div>
                                                            <div className="toggle-slider"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <ErrorMessage
                                                    name={`validations[${index}].maximumDuration`}
                                                    component="span"
                                                    className="validation-error"
                                                />
                                            </div>

                                            <div className="field-container">
                                                <label>Monthly Maximum</label>
                                                <Field
                                                    name={`validations[${index}].monthlyMaximum`}
                                                    type="number"
                                                    min={0}
                                                />
                                                <ErrorMessage
                                                    name={`validations[${index}].monthlyMaximum`}
                                                    component="span"
                                                    className="validation-error"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="add-section-btn"
                                    onClick={() =>
                                        push({
                                            zoneValidation: '',
                                            discount: '',
                                            maximumDuration: '',
                                            monthlyMaximum: 0,
                                            isFixedPrice: false
                                        })
                                    }
                                >
                                    Add Validation
                                </button>
                            </>
                        )}
                    </FieldArray>

                    <div className="buttons-row">
                        <Link onClick={goBack} className="back-to-home">
                            <IoIosArrowRoundBack />
                            <span>Back</span>
                        </Link>

                        <button type="submit" className="next-btn" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Next'}
                        </button>
                    </div>
                </FormikForm>
            )}
        </Formik>
    );
};

export default GroupRatesForm;
